import { useEffect, useState } from 'react';
import { useUser } from './useUser';
import { UserType } from '../types/common';
import companyApiService from '../services/companyApi';
import financialInstitutionApiService from '../services/financialInstitutionApi';

/**
 * Hook pour détecter automatiquement les associations existantes de l'utilisateur
 * et charger ses données depuis le backend
 * RÈGLE MÉTIER: Un utilisateur ne peut avoir qu'UNE SEULE entreprise OU institution financière
 */
export function useUserAssociations() {
  const { user, isLoading: isUserLoading } = useUser();
  const [detectedEntityType, setDetectedEntityType] = useState<UserType | null>(null);
  const [associations, setAssociations] = useState<{
    hasCompany: boolean;
    hasFinancialInstitution: boolean;
    companyId?: string;
    financialInstitutionId?: string;
  }>({
    hasCompany: false,
    hasFinancialInstitution: false
  });
  const [isCheckingAssociations, setIsCheckingAssociations] = useState(false);

  // Fonction pour vérifier les associations au backend (simplifiée)
  const checkUserAssociations = async (userId: string) => {
    setIsCheckingAssociations(true);
    
    try {
      console.log('🔍 Vérification des associations utilisateur (logique simplifiée):', userId);
      
      const results = {
        hasCompany: false,
        hasFinancialInstitution: false,
        companyId: undefined as string | undefined,
        financialInstitutionId: undefined as string | undefined
      };
      
      // Vérifier si l'utilisateur a une entreprise (UN SEUL)
      if (user?.companyId) {
        try {
          console.log('🏢 Vérification entreprise unique:', user.companyId);
          const company = await companyApiService.getCompany(user.companyId);
          if (company) {
            results.hasCompany = true;
            results.companyId = company.id;
            console.log('✅ Entreprise unique trouvée:', company.name);
          }
        } catch (error) {
          console.warn('⚠️ Entreprise non trouvée au backend:', error);
        }
      }
      
      // Vérifier si l'utilisateur a une institution financière (UN SEUL)
      if (user?.financialInstitutionId) {
        try {
          console.log('🏦 Vérification institution financière unique:', user.financialInstitutionId);
          const institution = await financialInstitutionApiService.getInstitution(user.financialInstitutionId);
          if (institution) {
            results.hasFinancialInstitution = true;
            results.financialInstitutionId = institution.id;
            console.log('✅ Institution financière unique trouvée:', institution.denominationSociale);
          }
        } catch (error) {
          console.warn('⚠️ Institution financière non trouvée au backend:', error);
        }
      }
      
      setAssociations(results);
      
      // Déterminer le type d'entité
      let entityType: UserType = 'sme'; // Par défaut
      
      if (results.hasFinancialInstitution) {
        entityType = 'financial_institution';
      } else if (results.hasCompany) {
        entityType = 'sme';
      } else if (user?.userType) {
        entityType = user.userType;
      }
      
      setDetectedEntityType(entityType);
      
      console.log('🎯 Associations détectées (règle: une seule entreprise par user):', {
        entityType,
        hasCompany: results.hasCompany,
        hasFinancialInstitution: results.hasFinancialInstitution,
        companyId: results.companyId,
        financialInstitutionId: results.financialInstitutionId
      });
      
    } catch (error) {
      console.error('❌ Erreur lors de la vérification des associations:', error);
    } finally {
      setIsCheckingAssociations(false);
    }
  };

  // Vérifier les associations quand l'utilisateur est chargé
  useEffect(() => {
    if (user && !isUserLoading && user.id) {
      checkUserAssociations(user.id);
    }
  }, [user?.id, isUserLoading]);

  return {
    user,
    isUserLoading,
    detectedEntityType,
    associations,
    isCheckingAssociations,
    recheckAssociations: () => {
      if (user?.id) {
        checkUserAssociations(user.id);
      }
    }
  };
}

/**
 * Hook simplifié pour détecter le type d'entité de l'utilisateur
 */
export function useUserEntityType(): {
  entityType: UserType | null;
  isLoading: boolean;
  hasExistingData: boolean;
} {
  const { detectedEntityType, associations, isCheckingAssociations, isUserLoading } = useUserAssociations();
  
  return {
    entityType: detectedEntityType,
    isLoading: isUserLoading || isCheckingAssociations,
    hasExistingData: associations.hasCompany || associations.hasFinancialInstitution
  };
}
